import React, { useEffect, useMemo, useRef, useState } from 'react';
import { uniqueId, debounce } from 'lodash';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { mailTemplatesUrl } from '@dashboard/services/env';
import usePreviewKey from '../usePreviewKey';
import getCheckoutPreviewUrl from '@dashboard/services/getCheckoutPreviewUrl';
import { usePreview } from '@dashboard/context/PreviewContext';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import Checkout from '@repo/checkout/Checkout';
import { checkoutLayouts, CheckoutStateProvider } from '@repo/checkout/hooks/useCheckoutState';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';
import CheckoutCSS from '@repo/checkout/components/CheckoutCSS';
import { XMarkIcon } from '@heroicons/react/24/solid';
import getUrlFromCdn, { CDNFitOption } from '@dashboard/services/getUrlFromCdn';
import { stripePublicKey, stripePublicKeyTest } from '@dashboard/services/env';
import { CheckoutRouterProvider } from '@repo/checkout/hooks/useCheckoutRouter';
import { useCountryCode } from '@repo/checkout/queries/country-code';
import { CheckoutLocalizationProvider } from '@repo/checkout/hooks/useCheckoutLocalization';
import TranslationProvider from '@repo/checkout/lib/intl/TranslationProvider';
import '@repo/checkout/lib/intl/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetcher } from '@/dashboard/services/request';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';

export default function Preview({ page, previewKey }) {
  const [preview] = usePreview();

  return (
    <div className="h-[calc(100vh-128px)]">
      <div className={clsx('h-full overflow-auto')}>
        {preview.type === 'test-checkout' && (
          <TestPaymentsCheckoutPreview page={page} previewKey={previewKey} />
        )}

        <div
          className={clsx('h-full mx-auto', {
            hidden: preview.type !== 'checkout' && preview.type !== 'confirmationCheckoutMessage',
          })}
        >
          <CheckoutPreview page={page} seller={page.seller} previewType={preview.type} />
        </div>

        {preview.type === 'confirmationEmailMessage' && (
          <PreviewWrapper backgroundColor={page.backgroundColor}>
            <ConfirmationEmailPreview page={page} />
          </PreviewWrapper>
        )}
      </div>
    </div>
  );
}

function TestPaymentsCheckoutPreview({ page, previewKey, previewType }) {
  const [{ variant }] = usePreview();

  const previewUrl = getCheckoutPreviewUrl(page, page.seller, {
    isPreview: true,
    isTesting: true,
    isTestingPayments: true,
    previewKey,
    variant: variant,
    isConfirmation: previewType === 'confirmationCheckoutMessage',
    usePageId: true,
  });

  let checkoutUrl = getCheckoutUrl(page, page.seller);

  return (
    <BrowserPreviewWrapper
      previewUrl={checkoutUrl}
      usageType={page.usageType}
      variant={variant}
      isTestingPayments={true}
    >
      <div
        className={clsx(
          'origin-top-left',
          'scale-50 w-[200%] h-[calc(100%/0.5)]',
          'xl:scale-75 xl:w-[133.33%] xl:h-[calc(100%/0.75)]',
          '3xl:scale-90 3xl:w-[111.11%] 3xl:h-[calc(100%/0.9)]',
          '4xl:scale-100 4xl:w-full 4xl:h-full',
          {
            '!h-full !w-full !mx-auto !scale-100':
              variant === 'site_embed' || variant === 'site_upsell_embed',
          },
        )}
      >
        <iframe src={previewUrl} className={clsx('w-full h-full')} />
      </div>
    </BrowserPreviewWrapper>
  );
}

function CheckoutPreview({ page }) {
  const [preview] = usePreview();
  let checkoutUrl = useMemo(() => getCheckoutUrl(page, page.seller), [page, page.seller]);
  const { token } = useAuth();

  const isConfirmation = preview.type === 'confirmationCheckoutMessage';

  const checkoutType = useMemo(() => {
    if (page.usageType === 'hosted') {
      return checkoutTypes.CHECKOUT;
    }

    if (page.usageType === 'site') {
      if (page.siteLayoutType === 'embed') {
        return checkoutTypes.EMBED;
      }

      if (page.siteLayoutType === 'popup') {
        return checkoutTypes.POPUP;
      }
    }

    return checkoutTypes.CHECKOUT;
  }, [page.usageType, page.siteLayoutType]);

  const pathname = useMemo(() => {
    if (preview.type === 'checkout') {
      return '/checkout';
    }

    return '/checkout/confirmation/:paymentId';
  }, [preview.type]);

  const pageUrlId = page.slug || page._id;
  const sellerUrlId = page.seller.name ? page.seller.name : page.seller._id;

  return (
    <>
      <BrowserPreviewWrapper
        key={page._id}
        previewUrl={checkoutUrl}
        variant={preview.variant}
        usageType={page.usageType}
        previewType={preview.type} // checkout
      >
        <div
          id={`checkout-preview-container-${page._id}`}
          className={clsx(
            'preview-container',
            'origin-top-left',
            'scale-50 w-[200%] h-[calc(200%)]',
            'xl:scale-75 xl:w-[133.33%] xl:h-[calc(133.33%)]',
            '3xl:scale-90 3xl:w-[111.11%] 3xl:h-[calc(111.11%)]',
            '4xl:scale-100 4xl:w-full 4xl:h-full',
          )}
        >
          <CheckoutPreviewComponent
            page={page}
            checkoutType={checkoutType}
            isConfirmation={isConfirmation}
            pathname={pathname}
            isPreview={true}
            isTesting={true}
            isTestingPayments={false}
          />
        </div>
      </BrowserPreviewWrapper>

      {page.status === 'published' && (
        <CheckoutEmbedMeasurer
          page={page}
          checkoutType={checkoutTypes.EMBED}
          isConfirmation={false}
          pathname={`/embed/${sellerUrlId}/${pageUrlId}`}
          token={token}
        />
      )}
    </>
  );
}

function CheckoutPreviewComponent({
  page,
  checkoutType,
  isConfirmation,
  pathname,
  isPreview = true,
  isTesting = true,
  isTestingPayments = false,
}) {
  const history = useHistory();
  const countryCode = useCountryCode();

  const localization = useMemo(() => {
    const locale = navigator.language;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = countryCode;

    return { locale, timezone, country };
  }, [countryCode]);

  const pageClass = useMemo(
    () =>
      clsx('cp_page flex flex-col h-full overflow-y-auto', {
        'bg-neutralgray': !page.backgroundColor,
      }),
    [page.backgroundColor],
  );

  const defaultWidth = 1920;
  const defaultHeight = 1080;

  return (
    <TranslationProvider>
      <CheckoutStateProvider
        value={{
          checkout: page,
          product: page.product,
          seller: page.seller,
          // upsell: page.upsell,
          // isUpsell: page.upsell,
          options: {
            type: checkoutType,
          },
          isPreview: isPreview,
          isTesting: isTesting,
          isTestingPayments: isTestingPayments,
          isScreenshot: false,
          query: {
            preview: isPreview,
          },
          countryCode,
          publicKey: stripePublicKey,
          publicKeyTest: stripePublicKeyTest,
          isConfirmation: isConfirmation,
        }}
      >
        {/* Use the original CheckoutCSS for variables */}
        <CheckoutCSS />

        {/* Add scoped CSS for preview container */}
        <ScopedCustomCSS
          containerId={`checkout-preview-container-${page._id}`}
          customCss={page.customCss}
        />

        {/* Add scoped CSS for embed measurer */}
        <ScopedCustomCSS
          containerId={`checkout-embed-measurer-${page._id}`}
          customCss={page.customCss}
        />

        <div className={pageClass}>
          <CheckoutRouterProvider
            router={{
              push: history.push,
              replace: history.replace,
              pathname,
              search: `?preview=true`,
            }}
          >
            <CheckoutLocalizationProvider localization={localization}>
              <Checkout />
            </CheckoutLocalizationProvider>
          </CheckoutRouterProvider>

          {checkoutType === checkoutTypes.CHECKOUT && (
            <div
              className="absolute inset-0 bg-fixed bg-no-repeat bg-cover bg-center -z-[1]"
              style={{
                ...(page.background === 'image' &&
                page.backgroundImage?.url &&
                page.hostedLayoutType !== checkoutLayouts.DOUBLE
                  ? {
                      backgroundImage: `image-set(url("${getUrlFromCdn(page.backgroundImage.url, {
                        width: defaultWidth,
                        height: defaultHeight,
                        fit: CDNFitOption.cover,
                      })}"), url("${getUrlFromCdn(page.backgroundImage.url, {
                        width: defaultWidth * 2,
                        height: defaultHeight * 2,
                        fit: CDNFitOption.cover,
                      })}") 2x)`,
                    }
                  : {}),
                ...(page.background === 'color' &&
                page.backgroundColor &&
                page.hostedLayoutType !== checkoutLayouts.DOUBLE
                  ? { background: page.backgroundColor }
                  : {}),
              }}
            />
          )}
        </div>
      </CheckoutStateProvider>
    </TranslationProvider>
  );
}

// we want to measure the height of the embed on various widths
// we will iterate over an array of widths and measure the height of the embed
// we will then store the height in an array
// we will then return the array of heights
export function CheckoutEmbedMeasurer({
  page,
  checkoutType,
  isConfirmation,
  pathname,
  onMeasurementsDone,
  isAdminMigration = false,
  measurementCoolDown = 6000,
  token,
}) {
  const ref = useRef(null);
  const widths = [360, 480, 600, 768, 1280];
  const [currentWidth, setCurrentWidth] = useState(widths[0]);
  const [measurements, setMeasurements] = useState([]);
  const [measurementState, setMeasurementState] = useState('rendered'); // rendered -> measuring -> saving
  const [currentMeasurementIndex, setCurrentMeasurementIndex] = useState(0);
  const lastMeasuredTime = useRef(0);
  const scheduledMeasurementTimeout = useRef(null);

  function getTimeUntilNextMeasurement() {
    const now = Date.now();
    const timeSinceLastMeasurement = now - lastMeasuredTime.current;
    return Math.max(0, measurementCoolDown - timeSinceLastMeasurement);
  }

  function startMeasuring() {
    setCurrentWidth(widths[0]);
    setCurrentMeasurementIndex(0);
    setMeasurements([]);
    setMeasurementState('measuring');
  }

  function scheduleMeasurement() {
    // Don't schedule if there's already a measurement scheduled
    if (scheduledMeasurementTimeout.current) {
      return;
    }

    const delay = getTimeUntilNextMeasurement();

    // Add 1 second to ensure component is fully rendered
    scheduledMeasurementTimeout.current = setTimeout(() => {
      startMeasuring();
      scheduledMeasurementTimeout.current = null;
    }, delay + 1000);
  }

  function hideStripeElements() {
    // set display: none on the express checkout element and payment element to prevent them from being measured
    const expressCheckoutElement = ref.current.querySelector('.cp_express-checkout-wrapper');
    const paymentElement = ref.current.querySelector('.cp_payment-element-wrapper');

    if (expressCheckoutElement && expressCheckoutElement.style.display !== 'none') {
      expressCheckoutElement.style.display = 'none';
      expressCheckoutElement.style.visibility = 'hidden';
      expressCheckoutElement.style.position = 'absolute';
      expressCheckoutElement.style.top = '0';
      expressCheckoutElement.style.left = '0';
      expressCheckoutElement.style.width = '0';
      expressCheckoutElement.style.height = '0';
    }

    if (paymentElement && paymentElement.style.display !== 'none') {
      paymentElement.style.display = 'none';
      paymentElement.style.visibility = 'hidden';
      paymentElement.style.position = 'absolute';
      paymentElement.style.top = '0';
      paymentElement.style.left = '0';
      paymentElement.style.width = '0';
      paymentElement.style.height = '0';
    }
  }

  function measureHeight() {
    if (measurementState !== 'measuring') return;

    hideStripeElements();

    const height = ref.current.offsetHeight;
    setMeasurements(prev => [...prev, { width: currentWidth, height }]);

    // Move to next width or finish measuring
    if (currentMeasurementIndex === widths.length - 1) {
      setMeasurementState('saving');
    } else {
      setCurrentMeasurementIndex(i => i + 1);
      setCurrentWidth(widths[currentMeasurementIndex + 1]);
    }
  }

  const { data: storedMeasurements } = useQuery({
    queryKey: ['checkoutEmbedMeasurements', page._id],
    queryFn: async () => {
      return fetcher(`/sellers/${page.seller._id}/pages/${page._id}/metadata`, token, {
        method: 'GET',
      });
    },
    enabled: !isAdminMigration,
  });

  const saveMeasurementsMutation = useMutation({
    mutationFn: async measurements => {
      return fetcher(`/sellers/${page.seller._id}/pages/${page._id}/metadata`, token, {
        method: 'POST',
        body: JSON.stringify({
          embedMeasurements: measurements,
        }),
      });
    },
    onSuccess: () => {
      lastMeasuredTime.current = Date.now();
      scheduledMeasurementTimeout.current = null;
      setMeasurementState('rendered');
      if (typeof onMeasurementsDone === 'function') {
        onMeasurementsDone(true);
      }
    },
    onError: error => {
      setMeasurementState('rendered');
      scheduledMeasurementTimeout.current = null;
      console.error('Error saving measurements:', error);
      if (typeof onMeasurementsDone === 'function') {
        onMeasurementsDone(false);
      }
    },
  });

  // Handle measurement state transitions
  useEffect(() => {
    if (measurementState === 'measuring') {
      const timeoutId = setTimeout(() => {
        measureHeight();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }

    if (measurementState === 'saving') {
      if (!storedMeasurements?.embedMeasurements) {
        saveMeasurementsMutation.mutateAsync(measurements);
      } else {
        const measurementsMatch = measurements.every(
          (measurement, index) =>
            measurement.height === storedMeasurements?.embedMeasurements?.[index]?.height,
        );

        if (!measurementsMatch || isAdminMigration === true) {
          saveMeasurementsMutation.mutateAsync(measurements);
        } else {
          setMeasurementState('rendered');
          scheduledMeasurementTimeout.current = null;
        }
      }
    }
  }, [measurementState, currentMeasurementIndex]);

  // Schedule new measurement when page changes or window is resized
  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      if (window.innerWidth >= 1024) {
        scheduleMeasurement();
      } else {
        // Clear any scheduled measurement if width goes below 1024
        if (scheduledMeasurementTimeout.current) {
          clearTimeout(scheduledMeasurementTimeout.current);
          scheduledMeasurementTimeout.current = null;
        }
      }
    }, 300);

    // Initial check (run immediately without debounce)
    if (window.innerWidth >= 1024) {
      scheduleMeasurement();
    } else {
      if (scheduledMeasurementTimeout.current) {
        clearTimeout(scheduledMeasurementTimeout.current);
        scheduledMeasurementTimeout.current = null;
      }
    }

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
      if (scheduledMeasurementTimeout.current) {
        clearTimeout(scheduledMeasurementTimeout.current);
        scheduledMeasurementTimeout.current = null;
      }
    };
  }, [page]);

  return (
    <div
      id={`checkout-embed-measurer-${page._id}`}
      style={{ position: 'absolute', left: -9999, top: 0, width: currentWidth }}
      ref={ref}
    >
      <CheckoutPreviewComponent
        page={page}
        checkoutType={checkoutType}
        isConfirmation={isConfirmation}
        pathname={pathname}
        isPreview={false}
        isTesting={true}
        isTestingPayments={true}
      />
    </div>
  );
}

// Create a component that only scopes the custom CSS
function ScopedCustomCSS({ containerId, customCss }) {
  const scopedCustomCss = useMemo(() => {
    if (!customCss) return '';

    // Prefix all selectors with the container ID to scope them
    return customCss.replace(/([^{]+)({[^}]*})/g, (match, selector, rules) => {
      // Skip @media queries and other at-rules
      if (selector.trim().startsWith('@')) return match;

      // Split multiple selectors and prefix each one
      const selectors = selector.split(',');
      const prefixedSelectors = selectors.map(s => `#${containerId} ${s.trim()}`).join(', ');

      return `${prefixedSelectors} ${rules}`;
    });
  }, [customCss, containerId]);

  return <style dangerouslySetInnerHTML={{ __html: scopedCustomCss }} />;
}

const BrowserPreviewWrapper = ({ children, previewUrl, variant, usageType, isTestingPayments }) => {
  const containerClassName = useMemo(
    () =>
      clsx('pb-4', {
        'h-full':
          isTestingPayments || (variant !== 'site_embed' && variant !== 'site_upsell_embed'),
        'max-w-screen-xs mx-auto': variant === 'site_embed' || variant === 'site_upsell_embed',
      }),
    [variant],
  );

  const contentClassName = useMemo(
    () =>
      clsx('pb-4', {
        'bg-neutralgray h-full':
          variant !== 'site_embed' && variant !== 'site_upsell_embed' && variant !== 'site_popup',
        'bg-black/40 h-full relative': variant === 'site_popup' && !isTestingPayments,
        'h-full': isTestingPayments,
      }),
    [variant],
  );

  return (
    <div className={containerClassName}>
      <div className="bg-white border border-gray-100 rounded-lg overflow-hidden h-full">
        <div className="relative flex items-center justify-between bg-white px-2 py-1 border-b border-gray-100 rounded-t-lg">
          <div className="absolute left-2 top-[7px] flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </div>

          <p className="text-gray-400 text-xs mx-auto w-2/3 truncate text-center">
            {usageType === 'site' && 'https://yourwebsite.com'}
            {usageType === 'hosted' && previewUrl ? previewUrl : '\u00A0'}
          </p>
        </div>

        <div className={contentClassName}>
          {variant === 'site_popup' && !isTestingPayments && (
            <button
              className="cp_pop-up-close ml-auto p-1 top-4 right-4 absolute transition-colors bg-neutralgray hover:bg-gray-100 rounded-full z-50"
              onClick={() => null}
              disabled
            >
              <XMarkIcon
                width="20px"
                height="20px"
                className="transition-colors fill-current text-gray-700"
              />
            </button>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

const PreviewWrapper = ({ backgroundColor, children }) => (
  <div
    className={clsx('cp_preview flex flex-col h-full overflow-y-auto', {
      'bg-neutralgray': !backgroundColor,
    })}
  >
    {children}
  </div>
);

function getEmailPreviewSrc(page, previewKey) {
  switch (page.type) {
    case 'form':
      return `${mailTemplatesUrl}/submission-confirmation-email/${page._id}?previewKey=${previewKey}`;

    case 'checkout':
      if (page.product?.type === 'charge') {
        return `${mailTemplatesUrl}/payment-confirmation-email/${page._id}?previewKey=${previewKey}`;
      }

      if (page.product?.type === 'subscription') {
        return `${mailTemplatesUrl}/subscription-confirmation-email/${page._id}?previewKey=${previewKey}`;
      }

      break;
    case 'event':
      return `${mailTemplatesUrl}/event-confirmation-email/${page._id}?previewKey=${previewKey}`;
    default:
      return ``;
  }
}

const ConfirmationEmailPreview = ({ page }) => {
  const [previewKey, setPreviewKey] = usePreviewKey();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // add timeout to ensure the page has been updated on the server
    // as page may already be updated optimistically
    setTimeout(() => {
      setPreviewKey(uniqueId());
    }, 1000);
  }, [
    page.confirmationEmailSubject,
    page.confirmationEmailMessage,
    page.customizeEmailConfirmation,
    page.sendEmailConfirmation,
    page.confirmationEmailShowLogo,
    page.confirmationEmailShowStoreName,
  ]);

  // useMemo to ensure the email preview src is not recalculated on every render
  const emailPreviewSrc = useMemo(() => getEmailPreviewSrc(page, previewKey), [previewKey]);

  return (
    <div className="h-full pb-4">
      <div className="bg-white border border-gray-100 rounded-lg overflow-hidden h-full relative">
        <div className="h-full">
          <iframe
            key={previewKey}
            src={emailPreviewSrc}
            className={clsx(
              'origin-top-left',
              'scale-50 w-[200%] h-[calc(100%/0.5)]',
              'xl:scale-75 xl:w-[133.33%] xl:h-[calc(100%/0.75)]',
              '3xl:scale-100 3xl:w-full 3xl:h-full',
            )}
          />
        </div>
      </div>
    </div>
  );
};
